import React, { useEffect, useRef, useState } from "react";
import { Tooltip } from "@mui/material";
import WrapperCard from "../../component-lib/Atoms/card/EventEHRWrapperCard";
import EventEHRSimpleCard from "../../component-lib/Molecules/card/EventEHRSimpleCard";
import Heading from "../../component-lib/Atoms/heading/EventEHRHeading";
import EventEHRItemListCard from "../../component-lib/Molecules/card/EventEHRItemListCard";
import Table from "../../component-lib/Organisms/table/EventEHRTable";
import { TEXT_CONSTANTS } from "../../utils/text-constants/textConstants";
import MenuDropdown from "../../component-lib/Molecules/menuDropdown/MenuDropdown";
import { useNavigate } from "react-router-dom";
import useHasOverflow from "../../custom-hooks/HasOverflow";
import { useAutoRefresh } from "../../custom-hooks/useAutoRefresh";
import EventEHRCreateVisitDialog from "../../component-lib/Organisms/create-visit-dialog/EventEHRCreateVisitDialog";
import Button from "../../component-lib/Molecules/button/Button";
import CreatePatientDrawer from "../patient-screen/create-patient-drawer/CreatePatientDrawer";
import { useDispatch, useSelector } from "react-redux";
import {
  getDashboardData,
  getVisitList,
  getFacilityRooms,
} from "./dashboardSaga";
import { componentKey, setClosePreviewDialog } from "./dashboardSlice";
import {
  componentKey as componentKeyDashboard,
  setRefreshPatientList,
  setCloseCreateTriageDrawer,
} from "../patient-screen/patientSlice";
import SaveTriageDialog from "../patient-screen/visit-flow/SaveTriageDialog";
import RoomAssignmentDialog from "../patient-screen/patient-details/dialog/RoomAssignmentDialog";
import {
  getFacilityStatus,
  getLoginUserData,
  getLoginUserId,
  getUserPermission,
  getFacilities,
  getFacilityId,
} from "../../utils/getToken";
import { capitalizeName } from "../../utils/capitalizeName";
import { isOffsiteTypeOfCare } from "../../utils/offsite/isOffsite";
import { isNurseTypeOfCare } from "../../utils/careType/isNurseTypeOfCare";
import FacilitySelector from "../../component-lib/Molecules/facility-selector/FacilitySelector";
import {
  destructStatusData,
  getStatusBackground,
  getStatusDotColor,
  getStatusTextColor,
  getSignTextColor,
  getSignBackground,
  getSignDotColor,
} from "../../utils/statusConstants";
import {
  getVisitNoteById,
  patchPatient,
  createTriage,
  deleteVisit,
} from "../patient-screen/patientSaga";
import DischargeFormPreviewModel from "../../utils/discharge-form-preview-model/DischargeFormPreviewModel";
import EventEHRConfirmationDialog from "../../component-lib/Molecules/confimation-dialog/EventEHRConfirmationDialog";
import {
  setDataToDischargeForm,
  setVisitNoteCareFlowData,
  setVisitPreviewDialog,
  setNotesData,
  setOrderChange,
} from "../patient-screen/patientSlice";
import { formatPhoneNumber } from "../../utils/phoneNumberFormatter";
import { getRoomDisplayName } from "../../utils/room/roomDisplay";
import ViewTaskDialog from "../tasks-screen/dialogs/ViewTaskDialog";
import moment from "moment";
import TooltipForTable from "../../component-lib/Molecules/tooltip/TooltipForTable";
import AddTasksDialog from "../tasks-screen/dialogs/AddTasksDialog";
import { updateTaskData } from "../tasks-screen/taskSaga";
import {
  componentKey as componentKeyTask,
  setCloseAddTaskDialog,
} from "../tasks-screen/taskSlice";
import EventEHRToggleTabButton from "../../component-lib/Molecules/toggle-tab-button/EventEHRToggleTabButton";
import { getFacilityInventoryList } from "../inventory-management-screen/InventorySaga";
import { componentKey as componentKeyInventory } from "../inventory-management-screen/InventorySlice";
import IcdCodesCard from "./icd-card/IcdCodesCard";

const DashboardScreen = () => {
  const { dashboardData, visitListData, closePreviewForDialog, facilityRooms } =
    useSelector((state: any) => state[componentKey]);

  // Show all facility rooms. Waiting & Expected rooms are pinned to the top
  // (Waiting first, then Expected); all other rooms follow in API order.
  const roomRank = (room: any) =>
    room?.isWaitingRoom ? 0 : room?.isExpectedRoom ? 1 : 2;
  const roomAssignmentsData = [...(facilityRooms ?? [])].sort(
    (a: any, b: any) => roomRank(a) - roomRank(b),
  );
  const {
    openVisitPreviewDialog,
    visitNoteCareFlowData,
    refreshPatientList,
    closeTriageDrawer,
  } = useSelector((state: any) => state[componentKeyDashboard]);

  const { closeAddTaskDialog } = useSelector(
    (state: any) => state[componentKeyTask],
  );

  const { inventoriesList } = useSelector(
    (state: any) => state[componentKeyInventory],
  );

  const userPermissionData: any = getUserPermission();

  const loginUserData: any = getLoginUserData();
  const parsedLoginUserData = JSON.parse(loginUserData);
  const loginUserFacilityId =
    parsedLoginUserData?.customAttributes?.facilityId || getFacilityId();

  // A visit can be deleted only by SuperAdmin/Admin users who also have
  // provider-portal access (isAdminAccess). Everyone else sees it disabled.
  const canDeleteVisit =
    (parsedLoginUserData?.roleNames?.includes("SuperAdmin") ||
      parsedLoginUserData?.roleNames?.includes("Admin")) &&
    parsedLoginUserData?.customAttributes?.isAdminAccess === "true";

  // SuperAdmin/Admin (with provider access) are not facility-specific, so they
  // may view/open any patient's visit note regardless of the selected facility.
  // Keyed off role — their own facilityId is blank — mirroring canDeleteVisit.
  const isFacilityAgnosticAdmin = canDeleteVisit;

  const loggedInUserName = capitalizeName(parsedLoginUserData?.name);

  const dispatch = useDispatch();
  const divRef = useRef<HTMLDivElement>(null);
  const hasOverflow = useHasOverflow(divRef);
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState(1);
  const [rowPerPage, setRowPerPage] = useState("5");
  const [openCreateVisitDialog, setOpenCreateVisitDialog] = useState(false);
  const [openPreviewForm, setOpenPreviewForm] = useState(false);
  const [isSignedVisit, setIsSignedVisit] = useState(false);
  const [openViewTaskDialog, setOpenViewTaskDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>({});
  const [showMsg, setShowMsg] = useState("");
  const [isCreatePatientDrawerOpen, setIsCreatePatientDrawerOpen] =
    useState(false);
  const [openTriage, setOpenTriage] = useState(false);
  const [triageIds, setTriageIds] = useState({
    visitId: "",
    patientId: "",
    typeOfCare: "",
  });
  const [openDeleteVisitConfirmDialog, setOpenDeleteVisitConfirmDialog] =
    useState(false);
  const [visitToDelete, setVisitToDelete] = useState<any>(null);
  const [openRoomAssignmentDialog, setOpenRoomAssignmentDialog] =
    useState(false);
  const [roomAssignmentData, setRoomAssignmentData] = useState<any>(null);
  // Dashboard vs Inventory Dashboard toggle + inventory table pagination.
  const [selectedDashboardTab, setSelectedDashboardTab] = useState("Dashboard");
  const [invPageNumber, setInvPageNumber] = useState(1);
  const [invRowPerPage, setInvRowPerPage] = useState("5");
  // Bumped by the 30s auto-refresh so the ICD card re-pulls its own window
  // without the dashboard needing to know which period the card is showing.
  const [icdRefreshKey, setIcdRefreshKey] = useState(0);
  const facilities = getFacilities();

  const parseLoginUserId: any = getLoginUserId();
  const loginUserId = JSON.parse(parseLoginUserId);

  const facilityStatus: any = getFacilityStatus();

  useEffect(() => {
    if (facilityStatus === "InActive") {
      setShowMsg(
        "Your facility is currently inactive. Please contact your administrator for further assistance. Until the facility is reactivated, you may have limited or no access to certain features.",
      );
    } else {
      setShowMsg("");
    }
  }, [facilityStatus]);

  const {
    totalVisit,
    EMSVisit,
    discharged,
    transferToJHC,
    transferToOffProperty,
    unsigned,
    patientDetils: patientDetails,
    taskDetails,
  } = dashboardData;

  const defaultTaskData = {
    taskType: "",
    assignBy: "",
    assignTo: "",
    assignedDate: "",
    dueDate: "",
    status: "",
  };

  // Dashboard simple cardS
  const cardsData = [
    { title: "Total Visits", value: totalVisit || "0", className: "" },
    { title: "EMS Visits", value: EMSVisit || "0", className: "" },
    { title: "Discharge", value: discharged || "0", className: "" },
    { title: "Expected", value: transferToJHC || "0", className: "" },
    {
      title: "Transfer Off Property",
      value: transferToOffProperty || "0",
      className: "",
    },
    {
      title: "Unsigned Visits",
      value: unsigned || "0",
      className: "font-medium text-primary-main cursor-pointer",
    },
  ];

  // Recent patient list data
  const listItemsData = patientDetails?.map((data: any) => ({
    title: `${data?.participant?.firstName || ""} ${data?.participant?.lastName || ""}`,
    subTitle: "BSA ID",
    valueOfSubTitle: data?.participant?.bsaId || "",
    imgURL: data?.participant?.profilePicture,
    room: getRoomDisplayName(data?.room?.room) || "Not assigned",
    id: data?.id,
  }));
  // To Do Task list data
  const todoTaskData = taskDetails?.map((data: any) => ({
    title: data?.taskType || "-",
    totalTask: 3,
    data: data,
  }));

  // RECENT VISIT COLUMN DATA
  const recentVisitColumns = [
    {
      field: "bsaId",
      label: <div>BSA ID</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal common-text-ellipse-style">
            {row?.bsaId}
          </p>
        );
      },
    },
    {
      field: "patientName",
      label: <div>Patient Name</div>,
      renderLogic: (row: any) => {
        return (
          <Tooltip
            title={
              row?.assignedProviderName
                ? `Assigned Provider: ${row?.assignedProviderName}`
                : "No provider assigned"
            }
            arrow
            placement="top"
          >
            <p className="font-medium text-neutral-90">{row?.patientName}</p>
          </Tooltip>
        );
      },
    },
    {
      field: "gender",
      label: <div>Gender</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.gender}</p>;
      },
    },
    {
      field: "visitType",
      label: <div>Visit Type</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.visitType}</p>;
      },
    },
    {
      field: "clinicLocation",
      label: <div>Unit Number</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">{row?.clinicLocation}</p>
        );
      },
    },
    {
      field: "chiefComplaint",
      label: <div>Chief Complaint</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            <TooltipForTable label={row?.chiefComplaint} />
          </p>
        );
      },
    },
    {
      field: "department",
      label: <div>Department</div>,
      renderLogic: (row: any) => {
        return <p className="text-neutral-90 font-normal">{row?.department}</p>;
      },
    },
    {
      field: "status",
      label: <div>Status</div>,
      renderLogic: (row: any) => {
        return (
          <div className="text-neutral-90 font-normal flex justify-start gap-1 items-center">
            <span
              className="font-weight-400 font-size-12px flex gap-1 items-center"
              style={{
                color: getStatusTextColor(row?.status),
                background: getStatusBackground(row?.status),
                borderRadius: "46px",
                padding: "4px 12px",
              }}
            >
              <span
                className="w-2 h-2"
                style={{
                  background: getStatusDotColor(row?.status),
                  borderRadius: "50%",
                }}
              ></span>
              {destructStatusData(row?.status)}
            </span>
          </div>
        );
      },
    },
    {
      field: "Signed/Unsigned",
      label: <div>Signed/Unsigned</div>,
      renderLogic: (row: any) => {
        return (
          <div className="text-neutral-90 font-normal flex justify-start gap-1 items-center">
            <span
              className="font-weight-400 font-size-12px flex gap-1 items-center"
              style={{
                color: getSignTextColor(row?.visitSigned),
                background: getSignBackground(row?.visitSigned),
                borderRadius: "46px",
                padding: "4px 12px",
              }}
            >
              <span
                className="w-2 h-2"
                style={{
                  background: getSignDotColor(row?.visitSigned),
                  borderRadius: "50%",
                }}
              ></span>
              {row?.visitSigned === "na"
                ? "N/A"
                : row?.visitSigned
                  ? "Signed"
                  : "Unsigned"}
            </span>
          </div>
        );
      },
    },
    {
      field: "action",
      label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
      renderLogic: (row: any) => {
        const options: any = [];

        let addNewVisitNoteOption: any = {};
        if (row?.visitNoteData?.length) {
          row?.visitNoteData?.map((data: any, index: number) => {
            if (
              data?.status === "discharged" ||
              data?.status === "transferOffProperty" ||
              data?.status === "transferToClinic" ||
              data?.status === "transferToJHC"
            ) {
              options.push({
                label: `View Visit Note ${index + 1}`,
                disabled: !userPermissionData?.visit?.view,
                onClickCb: () => {
                  const payload = {
                    id: row?.id,
                    comesFrom: "viewVisitNote",
                  };
                  dispatch(setNotesData(data));
                  dispatch(getVisitNoteById(payload));
                  dispatch(setOrderChange(true));
                  setIsSignedVisit(true);
                },
              });

              const hasDischarged = row?.visitNoteData?.some(
                (item: any) =>
                  item.status === "discharged" ||
                  item.status === "Draft" ||
                  item.status === "InDisposition" ||
                  !item?.isVisitSign,
              );

              // Transfer Off Property terminates the visit (no serial notes),
              // EXCEPT for EMS Scene visits — those keep "Add New Visit Note".
              // transferToClinic/transferToJHC/offsite still allow serial notes.
              if (!hasDischarged) {
                if (
                  data?.status === "transferToClinic" ||
                  data?.status === "transferToJHC" ||
                  (data?.status === "transferOffProperty" &&
                    row?.visitTypeRaw === "Scene") ||
                  isOffsiteTypeOfCare(data?.typeOfCare)
                ) {
                  const addNewNoteExists = options?.some(
                    (option: any) => option.label === "Add New Visit Note",
                  );

                  if (!addNewNoteExists) {
                    addNewVisitNoteOption = {
                      label: "Add New Visit Note",
                      disabled:
                        (!isFacilityAgnosticAdmin &&
                          !(
                            row?.isAssigned &&
                            row?.facilityId === loginUserFacilityId
                          )) || !userPermissionData?.visit?.create,
                      onClickCb: () => {
                        dispatch(setVisitNoteCareFlowData({}));
                        dispatch(setNotesData({}));
                        dispatch(setOrderChange(false));

                        if (isOffsiteTypeOfCare(row?.care1Copy)) {
                          navigate(
                            `/patient/${row?.patientId}/view-offsite-transfers-care-visit-note/${row?.id}?isAddNewNote=${true}`,
                          );
                        } else if (row?.care1Copy === "Self_Care") {
                          navigate(
                            `/patient/${row?.patientId}/view-self-care-visit-note/${row?.id}?isAddNewNote=${true}`,
                          );
                        } else if (isNurseTypeOfCare(row?.care1Copy)) {
                          navigate(
                            `/patient/${row?.patientId}/view-nurse-care-visit-note/${row?.id}?isAddNewNote=${true}`,
                          );
                        } else if (
                          row?.care1Copy?.toLowerCase()?.includes("mental")
                        ) {
                          navigate(
                            `/patient/${row?.patientId}/view-mental-health-care-visit-note/${row?.id}?isAddNewNote=${true}`,
                          );
                        } else if (
                          row?.care1Copy?.toLowerCase()?.includes("ear")
                        ) {
                          navigate(
                            `/patient/${row?.patientId}/view-listening-ear-care-visit-note/${row?.id}?isAddNewNote=${true}`,
                          );
                        } else {
                          navigate(
                            `/patient/${row?.patientId}/view-physician-care-visit-note/${row?.id}?isAddNewNote=${true}`,
                          );
                        }
                      },
                    };
                  }
                }
              }
            } else {
              options.push({
                label: "View Visit Note",
                disabled:
                  (!isFacilityAgnosticAdmin &&
                    !(
                      row?.isAssigned &&
                      row?.facilityId === loginUserFacilityId
                    )) || !userPermissionData?.visit?.view,
                onClickCb: () => {
                  dispatch(setVisitNoteCareFlowData({}));
                  dispatch(setNotesData(data));
                  dispatch(setOrderChange(true));

                  if (isOffsiteTypeOfCare(row?.care1Copy)) {
                    navigate(
                      `/patient/${row?.patientId}/view-offsite-transfers-care-visit-note/${row?.id}?isAddNewNote=${true}&newNoteStatus=${data?.status}`,
                    );
                  } else if (row?.care1Copy === "Self_Care") {
                    navigate(
                      `/patient/${row?.patientId}/view-self-care-visit-note/${row?.id}?isAddNewNote=${true}&newNoteStatus=${data?.status}`,
                    );
                  } else if (isNurseTypeOfCare(row?.care1Copy)) {
                    navigate(
                      `/patient/${row?.patientId}/view-nurse-care-visit-note/${row?.id}?isAddNewNote=${true}&newNoteStatus=${data?.status}`,
                    );
                  } else if (
                    row?.care1Copy?.toLowerCase()?.includes("mental")
                  ) {
                    navigate(
                      `/patient/${row?.patientId}/view-mental-health-care-visit-note/${row?.id}?isAddNewNote=${true}&newNoteStatus=${data?.status}`,
                    );
                  } else if (row?.care1Copy?.toLowerCase()?.includes("ear")) {
                    navigate(
                      `/patient/${row?.patientId}/view-listening-ear-care-visit-note/${row?.id}?isAddNewNote=${true}&newNoteStatus=${data?.status}`,
                    );
                  } else {
                    navigate(
                      `/patient/${row?.patientId}/view-physician-care-visit-note/${row?.id}?isAddNewNote=${true}&newNoteStatus=${data?.status}`,
                    );
                  }
                },
              });
            }
          });
          // Add "Add New Visit Note" at the end of the options if it's prepared
          if (addNewVisitNoteOption?.label) {
            options.push(addNewVisitNoteOption);
          }
          if (
            // Discharged ends the workflow. Transfer Off Property also terminates
            // the visit (no repeat vitals) EXCEPT for EMS Scene visits.
            row?.status !== "discharged" &&
            (row?.status !== "transferOffProperty" ||
              row?.visitTypeRaw === "Scene")
          ) {
            if (
              row?.care1Copy !== "Self_Care" &&
              !row?.care1Copy?.toLowerCase()?.includes("ear") &&
              !isOffsiteTypeOfCare(row?.care1Copy)
            ) {
              options?.push({
                label: row?.triage?.length ? "Add Rpt Vitals" : "Add Vitals",
                onClickCb: () => {
                  setTriageIds({
                    visitId: row?.id,
                    patientId: row?.patientId,
                    typeOfCare: row?.typeOfCare,
                  });
                  setOpenTriage(true);
                },
              });
            }
          }
        } else {
          options.push({
            label: "View Visit Note",
            disabled:
              (!isFacilityAgnosticAdmin &&
                !(row?.isAssigned && row?.facilityId === loginUserFacilityId)) ||
              !userPermissionData?.visit?.view,
            onClickCb: () => {
              dispatch(setNotesData({}));
              dispatch(setOrderChange(true));
              dispatch(setVisitNoteCareFlowData({}));
              navigateVisitNote(row?.care1Copy, row?.patientId, row?.id);
            },
          });
          if (
            row?.care1Copy !== "Self_Care" &&
            !row?.care1Copy?.toLowerCase()?.includes("ear") &&
            !isOffsiteTypeOfCare(row?.care1Copy)
          ) {
            options?.push({
              label: row?.triage?.length ? "Add Rpt Vitals" : "Add Vitals",
              onClickCb: () => {
                setTriageIds({
                  visitId: row?.id,
                  patientId: row?.patientId,
                  typeOfCare: row?.typeOfCare,
                });
                setOpenTriage(true);
              },
            });
          }
        }

        // Assign to me / Unassign — always available (not gated on triage),
        // so providers can take/continue ownership e.g. in the JHC transfer flow.
        options?.push({
          label: row?.isAssigned ? "Unassign" : "Assign to me",
          onClickCb: () => {
            const isAssigning = !row?.isAssigned;
            dispatch(
              patchPatient({
                id: row?.patientId,
                apiPayload: {
                  assigenProvider: JSON.stringify(!row?.isAssigned),
                },
              }),
            );
            // Auto-open Room Assignment when assigning a non-Self-Care patient.
            if (isAssigning && row?.care1Copy !== "Self_Care") {
              setRoomAssignmentData({
                patientId: row?.patientId,
                visitId: row?.id,
                facilityId: row?.facilityId,
                currentRoom: row?.room,
              });
              setOpenRoomAssignmentDialog(true);
            }
          },
          disabled: false,
        });

        // Room Assignment — for every care type except Self Care; available for
        // any patient at your facility (no provider assignment required).
        if (row?.care1Copy !== "Self_Care") {
          options?.push({
            label: "Room Assignment",
            disabled: !(row?.facilityId === loginUserFacilityId),
            onClickCb: () => {
              setRoomAssignmentData({
                patientId: row?.patientId,
                visitId: row?.id,
                facilityId: row?.facilityId,
                currentRoom: row?.room,
              });
              setOpenRoomAssignmentDialog(true);
            },
          });
        }

        // Delete visit — enabled only for SuperAdmin/Admin users with
        // provider-portal access; disabled (visible) for everyone else.
        options?.push({
          label: "Delete",
          disabled: !canDeleteVisit,
          onClickCb: () => {
            setVisitToDelete(row);
            setOpenDeleteVisitConfirmDialog(true);
          },
        });

        return (
          <>
            <div className="flex items-center">
              <MenuDropdown options={options} />
            </div>
          </>
        );
      },
    },
  ];

  const navigateVisitNote = (
    typeOfCare: string,
    patientId: string,
    visitId: string,
  ) => {
    // Visits created under the Offsite Transfers facility (typeOfCare = a
    // "hospital" department) use the Listening-Ear-style flow (serial notes,
    // no orders/vitals). A plain Transfer Off Property does NOT restyle the
    // visit — it routes by its own typeOfCare, like Transfer to JHC.
    if (isOffsiteTypeOfCare(typeOfCare)) {
      navigate(
        `/patient/${patientId}/view-offsite-transfers-care-visit-note/${visitId}`,
      );
    } else if (typeOfCare === "Self_Care") {
      navigate(`/patient/${patientId}/view-self-care-visit-note/${visitId}`);
    } else if (isNurseTypeOfCare(typeOfCare)) {
      navigate(`/patient/${patientId}/view-nurse-care-visit-note/${visitId}`);
    } else if (typeOfCare?.toLowerCase()?.includes("mental")) {
      navigate(
        `/patient/${patientId}/view-mental-health-care-visit-note/${visitId}`,
      );
    } else if (typeOfCare?.toLowerCase()?.includes("ear")) {
      navigate(
        `/patient/${patientId}/view-listening-ear-care-visit-note/${visitId}`,
      );
    } else {
      navigate(
        `/patient/${patientId}/view-physician-care-visit-note/${visitId}`,
      );
    }
  };

  // HANDLE DELETE VISIT
  const handleDeleteVisit = () => {
    if (visitToDelete?.id) {
      dispatch(deleteVisit({ visitId: visitToDelete.id }));
      setOpenDeleteVisitConfirmDialog(false);
      setVisitToDelete(null);
    }
  };

  // RECENT VISIT ROW DATA
  const recentVisitRowData = visitListData?.visit?.map((data: any) => ({
    id: data?.id,
    patientId: data?.patientId,
    typeOfCare: data?.typeOfCare,
    care1Copy: data?.typeOfCare,
    bsaId: data?.patient?.participant?.bsaId || "-",
    patientName: `${data?.patient?.participant?.firstName || "-"} ${data?.patient?.participant?.lastName || "-"}`,
    gender: data?.patient?.participant?.gender || "-",
    visitType: data?.type === "WALK_In_Visit" ? "Walk In" : "EMS",
    // Raw visit type ("Scene" = EMS Scene) — used to keep off-property EMS
    // visits alive while other off-property visits terminate.
    visitTypeRaw: data?.type,
    clinicLocation: data?.patient?.participant?.baseCamp?.unitNumber || "",
    status: data?.status || "-",
    chiefComplaint: data?.chiefComplaints || "-",
    department: data?.typeOfCare || "-",
    isAssigned: data?.patient?.isAssigned || false,
    facilityId: data?.patient?.facilityId,
    triage: data?.triage,
    visitNoteData: data?.visit_note,
    visitSigned:
      !data?.visit_note || data?.visit_note?.length === 0
        ? "na"
        : data?.visit_note?.some((item: any) => item?.isVisitSign === true),
    assignedProviderName: data?.assignedProviderName || null,
  }));

  useEffect(() => {
    if (closePreviewForDialog) {
      setOpenPreviewForm(false);
      setClosePreviewDialog(false);
    }
  }, [closePreviewForDialog]);

  // OPEN VIEW PREV  DIALOG
  useEffect(() => {
    if (openVisitPreviewDialog) {
      setOpenPreviewForm(true);
      dispatch(setVisitPreviewDialog(false));
      dispatch(
        setDataToDischargeForm({
          ...visitNoteCareFlowData,
        }),
      );
    }
  }, [openVisitPreviewDialog]);

  useEffect(() => {
    // Call visit count api here

    const fetchData = async () => {
      try {
        await Promise.all([dispatch(getDashboardData())]);
        // Process the results
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Fetch room assignments. facilityId is optional — pass it only when a
  // specific facility is selected. Changing the facility reloads the page
  // (see FacilitySelector), so this re-runs on mount with the new facilityId.
  useEffect(() => {
    dispatch(getFacilityRooms({ facilityId: getFacilityId() || undefined }));
  }, []);

  useEffect(() => {
    const payload = {
      pageNumber: pageNumber,
      limit: rowPerPage,
      view: "dashboard",
      include: "true",
    };
    dispatch(getVisitList(payload));
  }, [rowPerPage, pageNumber, closePreviewForDialog]);

  // Refresh recent visits after an assign/unassign toggle so the
  // "Assign to me"/"Unassign" label reflects the new isAssigned state.
  useEffect(() => {
    if (refreshPatientList) {
      dispatch(setRefreshPatientList(false));
      dispatch(
        getVisitList({
          pageNumber: pageNumber,
          limit: rowPerPage,
          view: "dashboard",
          include: "true",
        }),
      );
    }
  }, [refreshPatientList]);

  // Close the triage dialog and refetch recent visits after triage is saved.
  useEffect(() => {
    if (closeTriageDrawer) {
      setOpenTriage(false);
      dispatch(setCloseCreateTriageDrawer(false));
      dispatch(
        getVisitList({
          pageNumber: pageNumber,
          limit: rowPerPage,
          view: "dashboard",
          include: "true",
        }),
      );
    }
  }, [closeTriageDrawer]);

  // Inventory Dashboard data: count summary + low/out-of-stock lists +
  // paginated "All Inventory" list (dashboard=true response).
  const getInventoryDashboard = () => {
    dispatch(
      getFacilityInventoryList({
        facilityId: loginUserFacilityId,
        pageNumber: invPageNumber,
        limit: invRowPerPage,
        dashboard: "true",
      }),
    );
  };

  useEffect(() => {
    if (selectedDashboardTab === "Inventory Dashboard") {
      getInventoryDashboard();
    }
  }, [selectedDashboardTab, invPageNumber, invRowPerPage]);

  // Auto-refresh: silently re-fetch the dashboard's data every 30s so counts,
  // recent visits and room assignments stay current without a full-screen
  // spinner. Pauses while any dialog/drawer is open (so it can't disrupt an
  // in-progress action) and while the browser tab is hidden (handled in the
  // hook). The `silent` flag tells the sagas to skip showLoader/hideLoader.
  const isAnyDashboardDialogOpen =
    openVisitPreviewDialog ||
    openCreateVisitDialog ||
    openPreviewForm ||
    openViewTaskDialog ||
    openTriage ||
    openDeleteVisitConfirmDialog ||
    openRoomAssignmentDialog ||
    isCreatePatientDrawerOpen;

  useAutoRefresh(
    () => {
      dispatch(getDashboardData({ silent: true }));
      dispatch(
        getVisitList({
          pageNumber: pageNumber,
          limit: rowPerPage,
          view: "dashboard",
          include: "true",
          silent: true,
        }),
      );
      dispatch(
        getFacilityRooms({
          facilityId: getFacilityId() || undefined,
          silent: true,
        }),
      );
      setIcdRefreshKey((key) => key + 1);
    },
    { intervalMs: 30000, enabled: !isAnyDashboardDialogOpen },
  );

  // CREATE TRIAGE from the dashboard (mirrors PatientDetailsScreen mapping)
  const handleCreateTriageDialog = (formData: any) => {
    const payload = {
      apiPayload: {
        patientId: triageIds?.patientId,
        visitId: triageIds?.visitId,
        // Only send vitals that were actually entered (omit blanks).
        temperature: formData?.temperature
          ? Number(formData?.temperature)
          : undefined,
        oxygenSaturation: formData?.oxygenSaturation
          ? Number(formData?.oxygenSaturation)
          : undefined,
        heartRate: formData?.heathRate
          ? Number(formData?.heathRate)
          : undefined,
        respirationRate: formData?.respirationRate
          ? Number(formData?.respirationRate)
          : undefined,
        systolic: formData?.bloodPressure
          ? Number(formData?.bloodPressure)
          : undefined,
        diastolic: formData?.bloodPressure2
          ? Number(formData?.bloodPressure2)
          : undefined,
        woundCare: formData?.care === "woundCare" ? true : false,
        woundlocation:
          formData?.care === "woundCare" ? formData?.careValue : "",
        dehydration: formData?.care === "dehydration" ? true : false,
        dehydrationLevel:
          formData?.care === "dehydration" ? formData?.careValue : "",
        injury: formData?.care === "injury" ? true : false,
        injurylocation: formData?.care === "injury" ? formData?.careValue : "",
        other: formData?.care === "other" ? true : false,
        details: formData?.care === "other" ? formData?.careValue : "",
        triageTakenBy: formData?.triageTakenId,
        location: formData?.triageTakenLocation
          ? formData?.triageTakenLocation === "clinicLocation"
            ? "Clinic Location"
            : "JHC"
          : undefined,
      },
      comesFrom: "dashboard",
    };
    dispatch(createTriage(payload));
  };

  useEffect(() => {
    if (closeAddTaskDialog) {
      dispatch(setCloseAddTaskDialog(false));
      setOpenViewTaskDialog(false);
      dispatch(getDashboardData());
    }
  }, [closeAddTaskDialog]);

  const handleAddTaskData = (formData: any) => {
    try {
      const payload = {
        assigenTo: formData?.assignTo || undefined,
        assigenBy: loginUserId || undefined,
        taskType: formData?.taskType || undefined,
        status:
          formData?.status === "inProgress"
            ? "InProgress"
            : `${formData?.status.charAt(0).toUpperCase() + formData?.status.substring(1)}` ||
              undefined,
        dueDate: formData?.dueDate
          ? moment(formData?.dueDate).format("YYYY-MM-DD")
          : undefined,
        assigenDate: moment().format("YYYY-MM-DD"),
        description: formData?.taskDescription || undefined,
      };
      if (selectedTask?.status) {
        const taskUpdatePayload = {
          apiPayload: {
            ...payload,
          },
          taskId: selectedTask?.rowData?.id,
        };
        dispatch(updateTaskData(taskUpdatePayload));
      }
    } catch (error) {
      console.log(error);
    }
  };

  // ---- Inventory Dashboard derived data ----
  const inventoryCardsData = [
    {
      title: "Total Inventory",
      value: inventoriesList?.count?.totalStocks || "0",
    },
    { title: "In Stock", value: inventoriesList?.count?.inStock || "0" },
    { title: "Low Stock", value: inventoriesList?.count?.lowStock || "0" },
    { title: "Out of Stock", value: inventoriesList?.count?.outOfStock || "0" },
    { title: "Requested", value: inventoriesList?.count?.requested || "0" },
  ];

  const mapStockItem = (d: any) => ({
    title: d?.inventory?.itemName || "",
    subTitle: "Item ID:",
    valueOfSubTitle: d?.inventory?.itemCode || "",
  });
  const lowStockListItems =
    inventoriesList?.lowStocksInventory?.map(mapStockItem) || [];
  const outOfStockListItems =
    inventoriesList?.outOfStockInventory?.map(mapStockItem) || [];

  const allInventoryRowData = (inventoriesList?.data ?? []).map((d: any) => ({
    itemName: d?.inventory?.itemName || "-",
    category: d?.inventory?.type || "-",
    stockLevel: d?.currentStock ?? "0",
    reorderPoint: d?.threshold ?? "0",
    order: d?.orderQuantity ?? "0",
    status: d?.status ? d?.status?.split("_").join(" ") : "-",
    orderStatus: d?.orderStatus ? d?.orderStatus?.split("_").join(" ") : "-",
    expirationDate: d?.expirationDate
      ? // Date-only value (midnight UTC) — render in UTC so it never shifts.
        moment.utc(d?.expirationDate).format("MM/DD/YYYY")
      : "-",
    id: d?.id,
  }));

  const stockStatusChip = (status: string) => (
    <span
      className="font-weight-400 font-size-12px flex gap-1 items-center"
      style={{
        color: getStatusTextColor(status),
        background: getStatusBackground(status),
        borderRadius: "46px",
        padding: "4px 12px",
        width: "fit-content",
      }}
    >
      <span
        className="w-2 h-2"
        style={{ background: getStatusDotColor(status), borderRadius: "50%" }}
      ></span>
      {destructStatusData(status)}
    </span>
  );

  const allInventoryColumns = [
    {
      field: "itemName",
      label: <div>Item Name</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.itemName}</p>
      ),
    },
    {
      field: "category",
      label: <div>Category</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.category}</p>
      ),
    },
    {
      field: "stockLevel",
      label: <div>Stock Level</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.stockLevel}</p>
      ),
    },
    {
      field: "reorderPoint",
      label: <div>Reorder Point</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.reorderPoint}</p>
      ),
    },
    {
      field: "order",
      label: <div>Ordered Quantity</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.order}</p>
      ),
    },
    {
      field: "status",
      label: <div>Status</div>,
      renderLogic: (row: any) => stockStatusChip(row?.status),
    },
    {
      field: "orderStatus",
      label: <div>Order Status</div>,
      renderLogic: (row: any) => stockStatusChip(row?.orderStatus),
    },
    {
      field: "expirationDate",
      label: <div>Expiration Date</div>,
      renderLogic: (row: any) => (
        <p className="text-neutral-90 font-normal">{row?.expirationDate}</p>
      ),
    },
  ];

  return (
    // Fills the space left by App's flex column (no viewport math), and is
    // itself a column so the content area below the header can flex+scroll.
    <WrapperCard customClasses="m-3 p-3 flex-1 min-h-0 flex flex-col overflow-hidden">
      {/* Header wraps below xl so the action group drops to its own line on
          tablets/iPad instead of overflowing off the right edge. */}
      <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
        <div className="flex flex-col">
          {/* Login user Welcome Message */}
          <Heading
            title={`Welcome, ${loggedInUserName}`}
            customStyle={{
              color: "#393939",
              fontSize: "20px",
              whiteSpace: "nowrap",
            }}
          />
          {showMsg && (
            <div className=" bg-red-400 text-white p-[0.5px] mt-2  ">
              <span>{showMsg}</span>
            </div>
          )}
        </div>

        {/* Right-side actions: facility selector + create clinic visit.
            Takes the full row below xl (iPad portrait AND landscape) so the
            fixed-width controls can wrap instead of overflowing. */}
        <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto xl:justify-end">
          {facilities && facilities.length > 0 && (
            <FacilitySelector
              onFacilityChange={(facilityId) => {
                // console.log('Facility changed to:', facilityId);
              }}
            />
          )}
          <Button
            disabled={!userPermissionData?.patient?.create}
            label="Create Clinic Visit"
            variant="contained"
            onClickCb={() => {
              setIsCreatePatientDrawerOpen(true);
            }}
            style={{
              paddingTop: "6px",
              paddingBottom: "6px",
              minWidth: "155px",
            }}
          />
          {/* The toggle sets its width via inline style, so a Tailwind class
              can't override it — size it through this wrapper instead. */}
          <div className="w-full sm:w-[350px]">
            <EventEHRToggleTabButton
              customWidth="100%"
              variant="contained"
              defaultSelected={selectedDashboardTab}
              tabs={["Dashboard", "Inventory Dashboard"]}
              onToggle={(value: string) => setSelectedDashboardTab(value)}
            />
          </div>
        </div>
      </div>
      <div
        ref={divRef}
        className={`overflow-auto flex-1 min-h-0 ${hasOverflow ? "pr-2" : "0"}`}
      >
        {selectedDashboardTab === "Dashboard" && (
          <>
            {/* Dashboard cards */}
            <div className="flex w-full gap-3 mb-4">
              <EventEHRSimpleCard
                cardData={cardsData}
                handleClick={(data) => {
                  if (data?.title === "Unsigned Visits") {
                    navigate("/unsigned-visits");
                  }
                }}
              />
            </div>

            {/* Recent Patients, ICD/DSM Codes and Room Assignments — three
                equal columns (the ICD card takes half of the space Recent
                Patients used to have). */}
            <div className="flex flex-row gap-3 items-stretch">
              <div className="flex-1 min-w-0 flex">
                <EventEHRItemListCard
                  cardTitle="Recent Patients"
                  listItemsData={listItemsData}
                  itemsActionButtonName="View Profile"
                  handleItemsABClick={(data) => {
                    navigate(`/patient/${data?.id}`);
                  }}
                  handleViewAllClick={() => {
                    navigate(`/patient`);
                  }}
                  noDataDataFoundMessage="No Recent Patient Found "
                />
              </div>
              {/* ICD-10 / DSM codes being seen, by facility */}
              <div className="flex-1 min-w-0 flex">
                <IcdCodesCard refreshKey={icdRefreshKey} />
              </div>
              {/* Room Assignments (replaces the old To Do Tasks card) */}
              <div className="border-1px-solid-D2D2D2 rounded flex-1 min-w-0 flex flex-col">
                <div className="flex justify-between items-center m-3">
                  <div className="text-sm font-semibold text-[#565656]">
                    Room Assignments
                  </div>
                </div>
                <div className="px-3 pb-3 flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
                  {roomAssignmentsData?.length ? (
                    roomAssignmentsData.map((room: any) => (
                      <div
                        key={room?.id}
                        className="border border-[#E8E8E8] rounded-md p-2 mb-2"
                      >
                        <div className="flex justify-between items-center gap-2">
                          <span
                            className="text-sm font-semibold text-[#393939] truncate"
                            title={room?.name}
                          >
                            {room?.name}
                            {room?.roomNumber ? ` (#${room?.roomNumber})` : ""}
                          </span>
                          <span className="text-xs text-[#727272] whitespace-nowrap">
                            {room?.occupiedCount ??
                              room?.roomAssignments?.length ??
                              0}
                            {room?.capacity ? `/${room?.capacity}` : ""}
                          </span>
                        </div>
                        <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                          {room?.roomAssignments?.length ? (
                            room.roomAssignments.map((assignment: any) => {
                              const patient = assignment?.patient;
                              const provider = patient?.assignedProvider;
                              const patientName =
                                `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim() ||
                                "-";
                              const providerName = provider
                                ? `${provider?.firstName || ""} ${provider?.lastName || ""}`.trim()
                                : "-";
                              // previousAssignedProvider can be null.
                              const previousProvider =
                                patient?.previousAssignedProvider;
                              const previousProviderName = previousProvider
                                ? `${previousProvider?.firstName || ""} ${previousProvider?.lastName || ""}`.trim()
                                : "";
                              return (
                                <div
                                  key={assignment?.id}
                                  className="flex flex-col bg-[#F8F8F8] rounded px-2 py-1"
                                >
                                  <div className="flex items-center gap-1 text-xs">
                                    <span className="text-[#9A9A9A] whitespace-nowrap">
                                      Patient:
                                    </span>
                                    <span
                                      className="text-[#393939] font-medium truncate"
                                      title={patientName}
                                    >
                                      {patientName}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1 text-[11px]">
                                    <span className="text-[#9A9A9A] whitespace-nowrap">
                                      Assigned Provider:
                                    </span>
                                    <Tooltip
                                      arrow
                                      placement="top"
                                      title={
                                        <div className="flex flex-col gap-0.5">
                                          <span>
                                            Current: {providerName || "-"}
                                          </span>
                                          <span>
                                            Previous:{" "}
                                            {previousProviderName || "None"}
                                          </span>
                                        </div>
                                      }
                                    >
                                      <span className="text-[#727272] truncate cursor-default">
                                        {providerName}
                                      </span>
                                    </Tooltip>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <span className="text-xs text-[#BCBCBC]">
                              No patients assigned
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-[#BCBCBC] text-sm py-6">
                      No room assignments found
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Recent Visits Table Data */}
            <div className="mt-4">
              <Heading
                title="Recent Visits"
                customStyle={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#565656",
                }}
              />
              <div className="mt-2">
                <Table
                  columns={recentVisitColumns}
                  rows={recentVisitRowData}
                  noDataFoundMessage={"No recent visit found"}
                  paginationProps={{
                    isPagination: true,
                    page: pageNumber,
                    totalPages: visitListData?.totalPages,
                    totalCount: visitListData?.total,
                    limit: rowPerPage,
                    rowsPerPageOptions: [5, 10, 25],
                    onPageChange: (page: any) => {
                      setPageNumber(page);
                    },
                    handleChangeRowsPerPage: (rowPerPage: any) => {
                      setRowPerPage(rowPerPage);
                      setPageNumber(1);
                    },
                  }}
                />
              </div>
            </div>
          </>
        )}
        {selectedDashboardTab === "Inventory Dashboard" && (
          <>
            {/* Inventory summary cards */}
            <div className="flex w-full gap-3 mb-4">
              <EventEHRSimpleCard
                cardData={inventoryCardsData}
                handleClick={() => {}}
              />
            </div>
            {/* Low Stock and Out of Stock */}
            <div className="flex flex-row gap-3">
              <EventEHRItemListCard
                cardTitle="Low Stock"
                listItemsData={lowStockListItems}
                view={"withoutImage"}
                handleViewAllClick={() => navigate(`/inventory?status=Low_Stock`)}
                noDataDataFoundMessage="No Low Stock Found "
              />
              <EventEHRItemListCard
                cardTitle="Out of Stock"
                listItemsData={outOfStockListItems}
                view={"withoutImage"}
                handleViewAllClick={() =>
                  navigate(`/inventory?status=Out_of_Stock`)
                }
                noDataDataFoundMessage="No Out of Stock Found "
              />
            </div>
            {/* All Inventory Table */}
            <div className="mt-4">
              <div className="flex justify-between items-center">
                <Heading
                  title="All Inventory"
                  customStyle={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#565656",
                  }}
                />
                <Button
                  label="View All"
                  variant="text"
                  onClickCb={() => navigate(`/inventory`)}
                  style={{}}
                />
              </div>
              <div className="mt-2">
                <Table
                  columns={allInventoryColumns}
                  rows={allInventoryRowData}
                  noDataFoundMessage={"No inventory found"}
                  paginationProps={{
                    isPagination: true,
                    page: invPageNumber,
                    totalPages: inventoriesList?.totalPages,
                    totalCount: inventoriesList?.totalRecords,
                    limit: invRowPerPage,
                    rowsPerPageOptions: [5, 10, 25],
                    onPageChange: (page: any) => setInvPageNumber(page),
                    handleChangeRowsPerPage: (rpp: any) => {
                      setInvRowPerPage(rpp);
                      setInvPageNumber(1);
                    },
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>
      {/* CREATE VISIT DIALOG */}
      {openCreateVisitDialog && (
        <EventEHRCreateVisitDialog
          comesFrom={"dashboard"}
          dialogHeading={TEXT_CONSTANTS?.CREATE_VISIT}
          isOpen={openCreateVisitDialog}
          handleClose={() => {
            setOpenCreateVisitDialog(false);
          }}
          onEMSVisitSelected={() => navigate("/patient/jot-form")}
        />
      )}
      {/* CREATE CLINIC VISIT (PATIENT SEARCH) DRAWER */}
      {isCreatePatientDrawerOpen && (
        <CreatePatientDrawer
          open={isCreatePatientDrawerOpen}
          closeCb={() => {
            setIsCreatePatientDrawerOpen(false);
          }}
        />
      )}
      {openPreviewForm && (
        <DischargeFormPreviewModel
          comesFrom={"viewVisitNote"}
          signVisit={isSignedVisit}
          patientId={recentVisitRowData?.[0]?.id}
          visitNoteId={""}
          open={openPreviewForm}
          handleCloseModel={() => {
            setOpenPreviewForm(false);
            setIsSignedVisit(false);
          }}
        />
      )}

      {/* VIEW TASK DIALOG */}
      {openViewTaskDialog && (
        <AddTasksDialog
          defaultTaskData={selectedTask || defaultTaskData}
          dialogHeading={"Take Action"}
          isOpen={openViewTaskDialog}
          handleClose={() => {
            setOpenViewTaskDialog(false);
          }}
          handleAddTaskData={handleAddTaskData}
        />
      )}
      {openTriage && (
        <SaveTriageDialog
          ids={triageIds}
          open={openTriage}
          handleCloseDialog={() => setOpenTriage(false)}
          handleTriageSubmittedData={handleCreateTriageDialog}
        />
      )}
      {/* DELETE VISIT CONFIRMATION DIALOG */}
      <EventEHRConfirmationDialog
        alertTitle="Delete Visit"
        alertDescription="Are you sure you want to delete this visit? This permanently removes the visit and all of its related data (vitals, visit notes, orders, dispositions). This action cannot be undone."
        cancelButtonTitle="Cancel"
        submitButtonTitle="Delete"
        open={openDeleteVisitConfirmDialog}
        handleClose={(action) => {
          if (action === "close") {
            setOpenDeleteVisitConfirmDialog(false);
            setVisitToDelete(null);
          } else if (action === "submit") {
            handleDeleteVisit();
          }
        }}
      />
      {/* ROOM ASSIGNMENT DIALOG */}
      {openRoomAssignmentDialog && (
        <RoomAssignmentDialog
          isOpen={openRoomAssignmentDialog}
          handleClose={() => {
            setOpenRoomAssignmentDialog(false);
            setRoomAssignmentData(null);
          }}
          patientId={roomAssignmentData?.patientId}
          visitId={roomAssignmentData?.visitId}
          facilityId={roomAssignmentData?.facilityId}
          currentRoom={roomAssignmentData?.currentRoom}
        />
      )}
    </WrapperCard>
  );
};

export default DashboardScreen;
